import React from 'react';
import AnalyticsPreferences from '../components/AnalyticsPreferences';
import SEO from '../components/SEO';
import '../styles.css';

function PrivacyPage() {
  return (
    <div className="page-container">
      <SEO
        title="Virgil Privacy Policy"
        description="How Virgil collects, uses, shares, secures, and retains account, project, billing, analytics, and download data."
        url="https://www.virgil.com.ai/privacy"
      />
      <section className="content-section">
        <h1 className="page-title">Privacy Policy</h1>
        <div className="page-content legal-content">
          <p className="page-meta">Last updated: August 17, 2026</p>

          <h2>Scope</h2>
          <p>
            This policy explains how Virgil AI Inc. (&quot;Virgil,&quot;
            &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) handles
            information when you use our website, public beta, desktop
            software, support channels, and related services.
          </p>

          <h2>Information we collect</h2>
          <ul>
            <li>
              Account data such as your Auth0 subject, name, email address, and
              email-verification status.
            </li>
            <li>
              Project content, prompts, uploaded references, model responses,
              settings, and files you choose to save.
            </li>
            <li>
              Subscription details from Stripe, including customer and
              subscription IDs, plan, status, trial dates, and billing events.
              Virgil does not receive or store your full card number.
            </li>
            <li>
              Service and security data such as feature usage, errors, device
              and browser information, and rate-limit signals.
            </li>
            <li>
              Download records containing your account ID, platform, release
              version, time, outcome, and a one-way hash used for IP-based abuse
              prevention. Signed download URLs are not stored.
            </li>
            <li>Information you include in a support request.</li>
          </ul>

          <h2>How we use information</h2>
          <ul>
            <li>Authenticate accounts and enforce beta-seat and trial limits.</li>
            <li>Provide writing, formatting, project, billing, and download features.</li>
            <li>Route prompts and context to the model provider you select.</li>
            <li>Prevent abuse, investigate errors, and secure the service.</li>
            <li>Respond to support requests and required legal process.</li>
            <li>
              Measure signup and onboarding performance when you have allowed
              optional analytics.
            </li>
          </ul>

          <h2>Service providers</h2>
          <p>
            We use Auth0 for identity, Stripe for payments, MongoDB for service
            records, AWS/S3 for release delivery, PostHog for optional
            consent-based analytics, and AI providers such as OpenAI,
            Anthropic, Google, xAI, and providers of open models. We share only
            the data reasonably needed for each provider to perform its role.
            Their processing is also governed by their terms and privacy
            policies.
          </p>
          <p>
            Prompts and relevant project context are sent to the AI provider you
            choose to generate a response. Do not submit information you are not
            authorized to disclose. We do not sell your personal information.
          </p>

          <h2>Analytics and cookies</h2>
          <p>
            Essential storage is used for authentication, security, and your
            saved preferences. Optional product analytics remains off until you
            consent and is also disabled when analytics is not configured. Our
            events use an anonymous identifier and include public page names, plan
            or platform values, and broad referral categories such as ChatGPT,
            Claude, or a search engine. We do not send full referral URLs, search
            queries, names, email addresses, project content, payment details,
            or signed URLs in these events. After consent, the referral category
            is kept in this browser tab for up to 24 hours to connect a visit
            with signup and trial steps. Referrals may be unknown when a browser
            or app does not provide them.
          </p>

          <AnalyticsPreferences />

          <h2>Retention</h2>
          <p>
            Account and project data is generally kept while your account is
            active. Operational download and security events are normally kept
            for up to 90 days. Support records may be kept for up to two years.
            Billing and transaction records may be retained for up to seven
            years where needed for accounting, disputes, fraud prevention, or
            legal obligations. Deleted data may remain in encrypted backups for
            up to 30 additional days. We may retain a minimal record needed to
            enforce the one-trial eligibility rule. That record can include
            keyed, one-way identifiers derived from the verified email,
            account identifier, and Stripe card fingerprint; it does not contain
            the full card number.
          </p>

          <h2>Security</h2>
          <p>
            We use access controls, encryption in transit, restricted service
            credentials, short-lived download links, and monitoring intended to
            protect your information. No online service can guarantee absolute
            security, especially during a beta.
          </p>

          <h2>Your choices and rights</h2>
          <p>
            You can update certain account details, cancel a subscription, or
            request access, correction, export, or deletion by contacting us.
            Some records may be retained where required by law or needed for
            security, billing, dispute resolution, or trial-abuse prevention.
            Rights vary by location.
          </p>

          <h2>International processing and children</h2>
          <p>
            Providers may process information in the United States and other
            countries where they operate. Virgil is not directed to children
            under 13, and we do not knowingly collect their personal
            information.
          </p>

          <h2>Contact</h2>
          <p>
            Privacy or data requests:{' '}
            <a href="mailto:support@virgil.com.ai">support@virgil.com.ai</a>.
          </p>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPage;
